import {
  clearCartCacheEntry,
  clearCanceledGroupOrderCartCacheEntry,
  deletePaymentMethodCard,
  invalidateCartCacheEntry,
  setDefaultPaymentMethod,
  updateCartCache,
  updatePaymentMethod,
} from './utils';
import {
  addAddress,
  addAddressV2,
  deleteAddress,
  updateAddress,
  updateAddressV2,
} from './utils/addresses';

import type {
  AddLineItemToCartMutation,
  BagLeaveGroupOrderMutation,
  CartQuery,
  EditLineItemInCartMutation,
  GroupOrderCreateOrderMutation,
  GroupOrderJoinMutation,
  RemoveFromCartMutation,
  ReorderLineItemMutation,
  ReorderOrderMutation,
  SubmitBagOrderWithPaymentMethodMutation,
} from '../../../../graphql/operations.generated';
import type { UpdatesConfig } from '@urql/exchange-graphcache';

/**
 * Graphcache | Updates
 * A nested mapping of updater functions for mutation (and subscription) fields,
 * which may be used to add side-effects that update other parts of the cache
 * when the given subscription or mutation field is written to the cache.
 *
 * @see {@link: https://formidable.com/open-source/urql/docs/graphcache/cache-updates/}
 */

export const updates: UpdatesConfig = {
  Query: {
    cart(result: CartQuery, _args, cache) {
      clearCanceledGroupOrderCartCacheEntry(cache, result);
    },
  },
  Mutation: {
    // this force invalidates all the cache entries on logout
    logout(_result, _args, cache, _info) {
      cache.invalidate('Query');
      cache.invalidate('Mutation');
    },

    //
    // ─── CART UPDATES ────────────────────────────────────────────────
    //

    /*
      The following helpers modifies the 'cart' cache entry for the respective mutations.

      urql uses the `__typename` and `id` fields to update the cache entries,
      but sometimes we need to override the cart entry when the ID is different;
      therefore, we must manually handle these updates.

      Add here any cart-related update handler.
     */

    addLineItemToCart(result: AddLineItemToCartMutation, _args, cache) {
      updateCartCache(cache, result.addLineItemToCart);
    },
    editLineItemInCart(result: EditLineItemInCartMutation, _args, cache) {
      updateCartCache(cache, result.editLineItemInCart);
    },
    removeFromCart(result: RemoveFromCartMutation, _args, cache) {
      updateCartCache(cache, result.removeFromCart);
    },
    reorderOrder(result: ReorderOrderMutation, _args, cache) {
      updateCartCache(cache, result.reorderOrder);
    },
    reorderLineItem(result: ReorderLineItemMutation, _args, cache) {
      updateCartCache(cache, result.reorderLineItem);
    },
    createOrder(result: GroupOrderCreateOrderMutation, _args, cache) {
      const response = result.createOrder;

      if (response.__typename === 'CreateOrderSuccess') {
        updateCartCache(cache, { cart: response.order });
      }
    },
    joinMultipartyOrder(result: GroupOrderJoinMutation, _args, cache) {
      const response = result.joinMultipartyOrder;

      if (response.__typename === 'JoinMultipartyOrderSuccess') {
        updateCartCache(cache, { cart: response.order });
      }
    },
    leaveMultipartyOrder(result: BagLeaveGroupOrderMutation, _args, cache) {
      const response = result.leaveMultipartyOrder;

      if (response.__typename === 'LeaveOrderSuccess' && response.success) {
        clearCartCacheEntry(cache);
      }
    },
    submitOrderUsingPaymentMethod(result: SubmitBagOrderWithPaymentMethodMutation, _args, cache) {
      invalidateCartCacheEntry(cache, result.submitOrderUsingPaymentMethod);
    },

    // ─── Payment Methods Cache ─────────────────────────────────────────

    updatePaymentMethod,
    deletePaymentMethodCard,
    setDefaultPaymentMethod,

    // ─── Addresses Cache ───────────────────────────────────────────────

    addAddress,
    addAddressV2,
    updateAddress,
    updateAddressV2,
    deleteAddress,
  },
};
